// @ts-nocheck
// TODO: tighten types after migration
import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { useParams, useNavigate } from 'react-router-dom';
import { generatePodcastEpisodeStructuredData } from '../utils/structuredData';
import useFavicon from '../utils/useFavicon';
import { API_ENDPOINTS } from '../config/api';
import Header from '../Header';
import Footer from '../footer';
import Breadcrumbs from '../components/Breadcrumbs';
import { parseHtml } from '../utils/simpleHtmlParser';
import BlogLinkCard from '../components/BlogLinkCard';
import { parseChapters, getCurrentChapter } from '../utils/chapterParser';
import { renderTiptapHTML } from '../Blog/tiptapRenderer';
import { playPodcastGlobal } from './GlobalAudioPlayer';
import { getPodcastProgress, formatSecondsCompact, isSamePodcast, hydratePodcastProgressFromServer } from './playbackProgress';
import PodcastThoughts from './PodcastThoughts';
import SidebarContentGroup from '../components/SidebarContentGroup';
import TapeLoader from '../components/TapeLoader';
import './PodcastEpisode.css';
import '../Blog/AuthorPopup.css';
import moment from 'moment';
import useCopyFeedback from '../hooks/useCopyFeedback';
import { robustFetch } from '../utils/apiUtils';

const timestampToSeconds = (timestamp) => {
    if (typeof timestamp === 'number') return timestamp;
    if (typeof timestamp !== 'string' || !timestamp) return 0;
    
    const cleanTimestamp = timestamp.trim();
    const parts = cleanTimestamp.split(':').map(part => {
        const num = parseInt(part.trim(), 10);
        return isNaN(num) ? 0 : num;
    });
    
    if (parts.length === 3) {
        const hours = parts[0] || 0;
        const minutes = parts[1] || 0;
        const seconds = parts[2] || 0;
        return hours * 3600 + minutes * 60 + seconds;
    } else if (parts.length === 2) {
        const minutes = parts[0] || 0;
        const seconds = parts[1] || 0;
        return minutes * 60 + seconds;
    }
    return 0;
};

const getTranscriptWordStart = (word) => {
    if (!word) return null;

    let raw = null;
    if (word.start !== undefined && word.start !== null) raw = word.start;
    else if (word.startTime !== undefined && word.startTime !== null) raw = word.startTime;
    else if (word.timestamp !== undefined && word.timestamp !== null) raw = word.timestamp;

    if (raw === null) return null;

    let sec = 0;
    if (typeof raw === 'string') {
        sec = raw.includes(':') ? timestampToSeconds(raw) : parseFloat(raw);
    } else {
        sec = Number(raw);
    }

    if (!Number.isFinite(sec)) return null;

    if (sec > 100000) sec = sec / 1000;

    return Math.max(0, sec);
};

const STORAGE_KEY = 'globalPodcastPlayer';

export default function PodcastEpisode() {
    useFavicon("/images/home_images/Podcast 1 Thumbnail 4K.jpg");
    const { slug } = useParams();
    const navigate = useNavigate();
    const [podcast, setPodcast] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [showTranscript, setShowTranscript] = useState(true);
    const [viewMode, setViewMode] = useState('description'); // 'transcript' or 'description'
    const [transcriptData, setTranscriptData] = useState(null);
    const [transcriptLoading, setTranscriptLoading] = useState(false);
    const [transcriptError, setTranscriptError] = useState(null);
    const [currentTime, setCurrentTime] = useState(0);
    const [isGlobalPlaying, setIsGlobalPlaying] = useState(false);
    const [currentPlaybackDuration, setCurrentPlaybackDuration] = useState(0);
    const [isCurrentPodcast, setIsCurrentPodcast] = useState(false);
    const [isBuffering, setIsBuffering] = useState(false);
    const [thumbnailUrl, setThumbnailUrl] = useState(null);
    const [thumbnailRetryCount, setThumbnailRetryCount] = useState(0);
    const [activeWordInView, setActiveWordInView] = useState(true);
    const { isCopied, copyText } = useCopyFeedback({ resetDelayMs: 2000 });
    const [selectedAuthors, setSelectedAuthors] = useState([]);
    const [selectedAuthorIndex, setSelectedAuthorIndex] = useState(0);
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [isAutoScrollEnabled, setIsAutoScrollEnabled] = useState(true);
    const [chapters, setChapters] = useState([]);
    const [episodeProgress, setEpisodeProgress] = useState(null);
    const [, setCurrentChapter] = useState(null);
    const transcriptScrollRef = useRef(null);
    const lastUpdateSecondRef = useRef(-1);
    // Removed resize/maxWords state as we are now rendering full transcript

    useEffect(() => {
        const fetchPodcast = async () => {
            try {
                setLoading(true);
                setNotFound(false);
                const response = await robustFetch(API_ENDPOINTS.PODCAST.BY_SLUG(slug));
                
                if (!response.ok) {
                    if (response.status === 404) {
                        setNotFound(true);
                        setError('Podcast episode not found');
                    } else {
                        setError('Failed to load podcast episode');
                    }
                    setLoading(false);
                    return;
                }

                // Safe JSON parse — guards against Render.com cold-start returning "undefined"
                let data;
                try {
                    const rawText = await response.text();
                    const trimmed = rawText ? rawText.trim() : '';
                    if (!trimmed || trimmed === 'undefined' || trimmed === 'null') {
                        setError('Server returned an empty response. Please refresh.');
                        setLoading(false);
                        return;
                    }
                    data = JSON.parse(trimmed);
                } catch (parseErr) {
                    console.error('[PodcastEpisode] Failed to parse response:', parseErr);
                    setError('Failed to load podcast episode. Please refresh the page.');
                    setLoading(false);
                    return;
                }

                if (!data || typeof data !== 'object') {
                    setError('Invalid response from server. Please refresh.');
                    setLoading(false);
                    return;
                }

                // Fix thumbnail URL logic
                let correctedThumbnail = data.thumbnail;
                if (data.audioFile) {
                    try {
                        // Use the robust correction logic directly here or via helper
                        // Extract podcast number from audio file URL
                        const audioMatch = data.audioFile.match(/\/(?:Podcast-|episode-)(\d+)\//);
                        const podcastNumber = audioMatch ? audioMatch[1] : null;
                        
                        if (podcastNumber) {
                            const baseUrl = 'https://podcast-audio-episodes.s3.eu-north-1.amazonaws.com';
                            const correctPath = `episode-${podcastNumber}/Podcast+${podcastNumber}+Thumbnail.jpg`;
                            correctedThumbnail = `${baseUrl}/${correctPath}`;
                            console.log('[PodcastEpisode] Computed corrected thumbnail:', correctedThumbnail);
                        } else {
                             console.log('[PodcastEpisode] Could not extract podcast number from:', data.audioFile);
                        }
                    } catch (error) {
                        console.error('[PodcastEpisode] Error correcting thumbnail:', error);
                    }
                }
                
                if (correctedThumbnail) {
                    setThumbnailUrl(correctedThumbnail);
                    setThumbnailRetryCount(0);
                }
                
                setPodcast(data);
                
                // Parse Chapters
                const parsedChapters = parseChapters(data.overview);
                setChapters(parsedChapters);
                
                console.log('Podcast Data Debug:', data); // Debugging missing metadata
                document.title = `${data.title} | HariVishnu Insights`;
            } catch (err) {
                console.error('[PodcastEpisode] Error fetching podcast:', err);
                setError('Failed to load podcast episode');
            } finally {
                setLoading(false);
            }
        };

        if (slug) {
            fetchPodcast();
        }
    }, [slug]);

    useEffect(() => {
        const updatePlayerState = () => {
            const savedState = sessionStorage.getItem(STORAGE_KEY);
            if (savedState) {
                try {
                    const state = JSON.parse(savedState);
                    if (state.currentPodcast && podcast && isSamePodcast(state.currentPodcast, podcast)) {
                        setCurrentTime(state.currentTime || 0);
                        setCurrentPlaybackDuration(state.duration || 0);
                        setIsGlobalPlaying(state.isPlaying || false);
                        setIsBuffering(state.isBuffering || false);
                        setIsCurrentPodcast(true);
                        setEpisodeProgress(getPodcastProgress(podcast));
                        
                        // Update current chapter
                        setCurrentChapter(getCurrentChapter(chapters, state.currentTime || 0));
                    } else {
                        setIsCurrentPodcast(false);
                        setCurrentPlaybackDuration(0);
                        setIsGlobalPlaying(false);
                        setIsBuffering(false);
                        setEpisodeProgress(getPodcastProgress(podcast));
                        setCurrentChapter(null);
                    }
                } catch (e) {}
            } else {
                setIsCurrentPodcast(false);
                setCurrentPlaybackDuration(0);
                setIsGlobalPlaying(false);
                setIsBuffering(false);
                setEpisodeProgress(getPodcastProgress(podcast));
                setCurrentChapter(null);
            }
        };

        const handleAudioPlayingChange = (event) => {
            const state = event && event.detail;
            if (!state || !podcast) return;

            if (state.currentPodcast &&
                isSamePodcast(state.currentPodcast, podcast) &&
                state.audioPlayerVisible) {
                
                // Update state on EVERY detail change for 'lively' progress
                // Removed 1s throttle as per user request for lively progress bar
                setCurrentTime(state.currentTime || 0);
                setCurrentPlaybackDuration(state.duration || 0);
                setIsGlobalPlaying(!!state.isPlaying);
                setIsBuffering(!!state.isBuffering);
                setIsCurrentPodcast(true);
                setEpisodeProgress(getPodcastProgress(podcast));
                
                // Update current chapter
                setCurrentChapter(getCurrentChapter(chapters, state.currentTime || 0));
            } else if (isCurrentPodcast) {
                // Not the current podcast anymore
                setIsCurrentPodcast(false);
                setIsGlobalPlaying(false);
                setIsBuffering(false);
                setEpisodeProgress(getPodcastProgress(podcast));
                setCurrentChapter(null);
            }
        };

        updatePlayerState();
        void hydratePodcastProgressFromServer().then(() => setEpisodeProgress(getPodcastProgress(podcast)));
        const handleStorageChange = (e) => {
            if (e.key === STORAGE_KEY) {
                updatePlayerState();
            }
        };

        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('audioPlayingChange', handleAudioPlayingChange);
        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('audioPlayingChange', handleAudioPlayingChange);
        };
    }, [podcast, chapters]);

    const transcriptWords = useMemo(() => {
        const transcriptArray = transcriptData && transcriptData.transcript;
        if (!transcriptArray || !Array.isArray(transcriptArray) || transcriptArray.length === 0) {
            return { words: [], currentIndex: -1, isNewFormat: false };
        }

        const transcript = transcriptArray;
        let currentIndex = -1;
        
        const isNewFormat = transcript[0] && transcript[0].word !== undefined;
        
        if (isNewFormat) {
            // Find the active word using high-performance binary search
            let low = 0;
            let high = transcript.length - 1;
            
            while (low <= high) {
                const mid = Math.floor((low + high) / 2);
                const wordStart = getTranscriptWordStart(transcript[mid]);
                
                if (wordStart !== null && wordStart <= currentTime) {
                    currentIndex = mid;
                    low = mid + 1;
                } else {
                    high = mid - 1;
                }
            }

            if (currentIndex === -1 && transcript.length > 0) {
                currentIndex = 0;
            }

            return { words: transcript, currentIndex, isNewFormat: true };
        } else {
            return { words: transcript, currentIndex: -1, isNewFormat: false };
        }
    }, [transcriptData, currentTime]);

    // Check visibility of active word
    const checkActiveWordVisibility = React.useCallback(() => {
        if (!showTranscript || !transcriptWords.isNewFormat || transcriptWords.currentIndex < 0) return;

        const element = document.getElementById(`episode-transcript-word-${transcriptWords.currentIndex}`);
        const container = document.getElementById('episode-transcript-scroll-area');

        if (element && container) {
            const containerRect = container.getBoundingClientRect();
            const elementRect = element.getBoundingClientRect();

            const isVisible = (
                elementRect.top >= containerRect.top - 20 &&
                elementRect.bottom <= containerRect.bottom + 20
            );

            setActiveWordInView(isVisible);
        }
    }, [showTranscript, transcriptWords.currentIndex, transcriptWords.isNewFormat]);

    // Update visibility and auto-scroll
    useEffect(() => {
        checkActiveWordVisibility();

        if (showTranscript && isAutoScrollEnabled && transcriptWords.isNewFormat && transcriptWords.currentIndex >= 0) {
            const currentElement = document.getElementById(`episode-transcript-word-${transcriptWords.currentIndex}`);
            const container = document.getElementById('episode-transcript-scroll-area');
            if (currentElement && container) {
                const containerRect = container.getBoundingClientRect();
                const elementRect = currentElement.getBoundingClientRect();
                const scrollTop = container.scrollTop;
                const elementOffset = elementRect.top - containerRect.top + scrollTop;
                const centerOffset = (containerRect.height / 2) - (elementRect.height / 2);
                const targetTop = elementOffset - centerOffset;

                // Smoothly scroll container using same logic as global player
                if (Math.abs(container.scrollTop - targetTop) > 10) {
                    container.scrollTo({
                        top: targetTop,
                        behavior: isGlobalPlaying ? 'auto' : 'smooth'
                    });
                }
            }
        }
    }, [transcriptWords.currentIndex, showTranscript, checkActiveWordVisibility, isAutoScrollEnabled, transcriptWords.isNewFormat, isGlobalPlaying]);

    const handleCopyLink = () => {
        const url = window.location.href;
        void copyText(url);
    };

    // Helper to extract a URL from an image field (string or [{url, ...}] array)
    const extractImageUrl = (imgField) => {
        if (!imgField) return null;
        if (typeof imgField === 'string') return imgField;
        if (Array.isArray(imgField) && imgField.length > 0) {
            for (let i = imgField.length - 1; i >= 0; i--) {
                const img = imgField[i];
                if (img && img.url && img.imageTitle) return img.url;
            }
            for (let i = imgField.length - 1; i >= 0; i--) {
                const img = imgField[i];
                if (img && img.url) return img.url;
            }
        }
        // Handle object with url property or numeric keys
        if (typeof imgField === 'object' && !Array.isArray(imgField)) {
            if (imgField.url) return imgField.url;
            const keys = Object.keys(imgField).sort((a, b) => parseInt(a) - parseInt(b));
            for (const key of keys) {
                if (imgField[key] && imgField[key].url) return imgField[key].url;
            }
        }
        return null;
    };

    const extractSocialLinks = (links) => {
        if (!links) return [];
        if (Array.isArray(links)) return links;
        if (typeof links === 'string') return [links];
        if (typeof links === 'object') {
            const keys = Object.keys(links).sort((a, b) => parseInt(a) - parseInt(b));
            const result = [];
            for (const key of keys) {
                if (typeof links[key] === 'string') result.push(links[key]);
                else if (links[key] && links[key].url) result.push(links[key].url);
            }
            if (result.length > 0) return result;
            if (links.url) return [links.url];
        }
        return [];
    };

    const handleAuthorClick = (hosts, index = 0) => {
        // Map host data to the format expected by the AuthorPopup
        const mappedAuthors = hosts.map(host => ({
            name: host.hostName || host.name,
            image: extractImageUrl(host.hostImage) || extractImageUrl(host.image),
            bio: host.hostBio || host.bio || "Host of this podcast episode.",
            socialLinks: extractSocialLinks(host.socialLinks)
        }));

        setSelectedAuthors(mappedAuthors);
        setSelectedAuthorIndex(index);
        setIsPopupOpen(true);
    };

    const handlePopupClose = () => {
        setIsPopupOpen(false);
        setSelectedAuthors([]);
        setSelectedAuthorIndex(0);
    };

    const handleUserScrollInteraction = () => {
        if (isAutoScrollEnabled) {
            setIsAutoScrollEnabled(false);
        }
    };

    const getEpisodeButtonUiState = () => {
        const progress = episodeProgress || getPodcastProgress(podcast);
        const livePlayed = isCurrentPodcast ? currentTime : (progress?.currentTime || 0);
        const liveDuration = isCurrentPodcast ? (currentPlaybackDuration || progress?.duration || 0) : (progress?.duration || 0);
        const completed = !!progress?.completed;
        const started = !!(progress?.started || livePlayed > 1);
        const leftSeconds = liveDuration > 0 ? Math.max(0, liveDuration - livePlayed) : 0;
        const progressPct = liveDuration > 0 ? Math.min(100, Math.max(0, (livePlayed / liveDuration) * 100)) : 0;

        if (completed) {
            return { mode: 'completed', leftText: '0s', progressPct: 100 };
        }
        if (started || isCurrentPodcast) {
            return { mode: 'progress', leftText: formatSecondsCompact(leftSeconds), progressPct, isLive: isCurrentPodcast && isGlobalPlaying };
        }
        return { mode: 'idle', leftText: '', progressPct: 0, isLive: false };
    };

    // Fetch transcript on demand (called when user clicks "Transcript" tab)
    const fetchTranscript = async () => {
        if (transcriptData || transcriptLoading) return;
        setTranscriptLoading(true);
        setTranscriptError(null);
        try {
            const response = await robustFetch(API_ENDPOINTS.PODCAST.TRANSCRIPT(slug));
            const rawText = await response.text();
            const trimmed = rawText ? rawText.trim() : '';
            if (!trimmed || trimmed === 'undefined' || trimmed === 'null') {
                setTranscriptData({ transcript: [] });
                return;
            }
            let data;
            try { data = JSON.parse(trimmed); } catch (e) {
                setTranscriptError('Failed to parse transcript.');
                return;
            }
            setTranscriptData(data);
        } catch (err) {
            console.error('[PodcastEpisode] Error fetching transcript:', err);
            setTranscriptError('Failed to load transcript. Please try again.');
        } finally {
            setTranscriptLoading(false);
        }
    };

    if (loading) {
        return (
            <>
                <Header />
                <TapeLoader />
                <Footer />
            </>
        );
    }

    if (error || !podcast) {
        const isHardNotFound = notFound || error === 'Podcast episode not found';
        return (
            <>
                <Header />
                <Breadcrumbs />
                <Helmet>
                    <title>Podcast Not Found | Hari Vishnu</title>
                    <meta name="robots" content={isHardNotFound ? 'noindex' : 'index,follow'} />
                </Helmet>
                <div className="podcast-episode-error mx-auto my-10 w-full max-w-2xl rounded-2xl bg-white p-6 text-center shadow-sm">
                    <h2>{isHardNotFound ? 'Podcast episode not found' : (error || 'Podcast episode temporarily unavailable')}</h2>
                    <button onClick={() => navigate('/podcast')}>Back to Podcasts</button>
                </div>
                <Footer />
            </>
        );
    }

    const podcastCanonical = `https://www.harivishnu.in/podcast/${podcast.slug}`;
    const rawPodcastImage = thumbnailUrl || podcast.thumbnail || "https://www.harivishnu.in/images/home_images/Logo.png";
    const podcastImage = rawPodcastImage.startsWith('http') 
        ? rawPodcastImage 
        : `https://www.harivishnu.in${rawPodcastImage.startsWith('/') ? '' : '/'}${rawPodcastImage}`;
    const podcastDescription = typeof podcast.description === 'string'
        ? podcast.description.slice(0, 160)
        : `Listen to ${podcast.title} on Hari Vishnu Insights Podcast.`;

    const podcastSchema = generatePodcastEpisodeStructuredData({
        title: podcast.title,
        description: podcastDescription,
        slug: podcast.slug,
        thumbnailUrl: podcastImage,
        audioFileUrl: podcast.audioFile,
        createdAt: podcast.createdAt,
        episodeNumber: podcast.episodeNumber || podcast.podcastNumber,
    });

    const podcastBreadcrumbLd = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.harivishnu.in/" },
            { "@type": "ListItem", "position": 2, "name": "Podcast", "item": "https://www.harivishnu.in/podcast" },
            { "@type": "ListItem", "position": 3, "name": podcast.title, "item": podcastCanonical }
        ]
    };

    return (
        <>
            <Header />
            <Breadcrumbs />
            <Helmet>
                <title>{podcast.title} | Hari Vishnu Insights Podcast</title>
                <meta name="description" content={podcastDescription} />
                <meta name="robots" content="index,follow" />
                <link rel="canonical" href={podcastCanonical} />

                {/* Open Graph / Facebook */}
                <meta property="og:type" content="article" />
                <meta property="og:url" content={podcastCanonical} />
                <meta property="og:title" content={`${podcast.title} | Hari Vishnu Insights`} />
                <meta property="og:description" content={podcastDescription} />
                <meta property="og:image" content={podcastImage} />
                <meta property="og:site_name" content="Hari Vishnu Insights" />
                <meta property="og:audio" content={podcast.audioFile || ''} />

                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:url" content={podcastCanonical} />
                <meta name="twitter:title" content={`${podcast.title} | Hari Vishnu Insights`} />
                <meta name="twitter:description" content={podcastDescription} />
                <meta name="twitter:image" content={podcastImage} />

                {/* Structured Data (PodcastEpisode schema) */}
                <script type="application/ld+json">
                    {JSON.stringify(podcastSchema)}
                </script>
                <script type="application/ld+json">
                    {JSON.stringify(podcastBreadcrumbLd)}
                </script>
            </Helmet>
            <div className="podcast-episode-page bg-slate-50">
                <div className="podcast-episode-container mx-auto grid w-full max-w-7xl gap-5 px-3 pb-8 pt-6 lg:grid-cols-[1fr_320px] md:px-5">
                    <div className="podcast-episode-content min-w-0 rounded-2xl bg-white/90 p-3 shadow-sm md:p-5">
                        <div className="episode-header">
                            <div className="episode-primary-content">
                                <div className="episode-thumbnail-desktop">
                                {thumbnailUrl ? (
                                    <img 
                                        key={thumbnailUrl}
                                        src={thumbnailUrl}
                                        alt={podcast.title}
                                        style={{ display: 'block', maxWidth: '100%' }}
                                        loading="lazy"
                                        onError={(e) => {
                                            if (thumbnailRetryCount === 0 && podcast.thumbnail) {
                                                let nextUrl = null;
                                                // Try replacing + with %20 (common S3 issue)
                                                if (podcast.thumbnail.includes('+')) {
                                                    nextUrl = podcast.thumbnail.replace(/\+/g, '%20');
                                                }
                                                // If no + to replace, or if we want to try the original as a backup
                                                if (!nextUrl) nextUrl = podcast.thumbnail;
                                                
                                                if (nextUrl && nextUrl !== thumbnailUrl) {
                                                    console.log('[PodcastEpisode] Retrying thumbnail with:', nextUrl);
                                                    setThumbnailUrl(nextUrl);
                                                    setThumbnailRetryCount(1);
                                                } else {
                                                    // If no variation possible, fail
                                                    setThumbnailUrl(null);
                                                }
                                            } else {
                                                console.log('[PodcastEpisode] Thumbnail failed to load after retries.');
                                                setThumbnailUrl(null); // Show placeholder
                                            }
                                        }}
                                    />
                                ) : (
                                    <div style={{ width: '100%', height: '300px', backgroundColor: '#e5e7eb', borderRadius: '15px' }}></div>
                                )}
                            </div>
                            <div className="episode-details">
                                <h1 className="episode-title">{podcast.title}</h1>
                                <div className="episode-description-header">
                                    <div className="episode-description">
                                        {(() => {
                                            if (!podcast.description) return 'Listen to this podcast episode.';
                                            if (typeof podcast.description === 'object' && podcast.description.json) {
                                                try {
                                                    const html = renderTiptapHTML(podcast.description);
                                                    if (html) return <div dangerouslySetInnerHTML={{ __html: html }} />;
                                                } catch (e) {
                                                    console.error("Error rendering description:", e);
                                                }
                                            }
                                            return typeof podcast.description === 'string' ? podcast.description : 'Listen to this podcast episode.';
                                        })()}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="podcast-meta-container">
                            <div className="podcast-meta-row">
                                <div className="podcast-meta-left">
                                    {/* Big author image - Overlapping for multiple hosts */}
                                    <div className="podcast-author-image-large">
                                        {podcast.hosts && podcast.hosts.length > 0 && (
                                            <div className={`podcast-author-images-container ${podcast.hosts.length > 1 ? 'overlapped' : ''}`}>
                                                {podcast.hosts.map((host, idx) => (
                                                    <img
                                                        key={idx}
                                                        className="podcast-author-img-large"
                                                        src={extractImageUrl(host.hostImage) || extractImageUrl(host.image)}
                                                        alt={`${host.hostName || host.name} profile`}
                                                        onClick={() => handleAuthorClick(podcast.hosts, idx)}
                                                        style={{
                                                            zIndex: podcast.hosts.length - idx,
                                                            marginLeft: idx > 0 ? '-25px' : '0',
                                                            cursor: 'pointer'
                                                        }}
                                                        onError={(e) => {
                                                            e.target.src = "/images/home_images/Logo.png";
                                                        }}
                                                    />
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <div className="podcast-author-meta-info">
                                        <span className="podcast-author-name-large">
                                            {podcast.hosts && podcast.hosts.length > 1 ? (
                                                <span 
                                                    onClick={() => handleAuthorClick(podcast.hosts, 0)}
                                                    style={{ cursor: 'pointer' }}
                                                    className="podcast-host-name-link"
                                                >
                                                    Hosts
                                                </span>
                                            ) : (
                                                podcast.hosts && podcast.hosts.length > 0 ? (
                                                    podcast.hosts.map((host, index) => (
                                                        <span 
                                                            key={index} 
                                                            onClick={() => handleAuthorClick(podcast.hosts, index)}
                                                            style={{ cursor: 'pointer' }}
                                                            className="podcast-host-name-link"
                                                        >
                                                            {host.hostName || host.name}
                                                        </span>
                                                    ))
                                                ) : "Hari Vishnu"
                                            )}
                                        </span>
                                        <div className="podcast-author-meta-details">
                                            <span className="podcast-date-issue">
                                                {moment(podcast.date || podcast.createdAt).fromNow()}
                                            </span>
                                            
                                            {(() => {
                                                // Duration Logic: Try podcast.duration first, then calculate from transcript
                                                let displayDuration = podcast.duration;
                                                
                                                if (!displayDuration && transcriptData && transcriptData.transcript && transcriptData.transcript.length > 0) {
                                                    const lastItem = transcriptData.transcript[transcriptData.transcript.length - 1];
                                                    if (lastItem && (lastItem.end || lastItem.start)) {
                                                        const seconds = lastItem.end || lastItem.start;
                                                        const durationMoment = moment.duration(seconds, 'seconds');
                                                        if (durationMoment.asHours() >= 1) {
                                                            displayDuration = `${Math.floor(durationMoment.asHours())}h`;
                                                        } else if (durationMoment.asMinutes() >= 1) {
                                                            displayDuration = `${Math.floor(durationMoment.asMinutes())}m`;
                                                        } else {
                                                            displayDuration = `${Math.floor(durationMoment.asSeconds())}s`;
                                                        }
                                                    }
                                                }
                                                
                                                if (displayDuration) {
                                                    return (
                                                        <>
                                                            <div className="podcast-dot-container">
                                                                <span className="podcast-dot"></span>
                                                            </div>
                                                            <span className="podcast-meta-item">
                                                                <i className="fi fi-rr-clock"></i>
                                                                {displayDuration}
                                                            </span>
                                                        </>
                                                    );
                                                }
                                                return null;
                                            })()}
                                        </div>
                                    </div>
                                </div>

                                {/* Right side: Share section */}
                                <div className="podcast-meta-right">
                                    <div className="podcast-share-text">Share</div>
                                    <button 
                                        className={`podcast-copy-btn ${isCopied ? 'copied' : ''}`}
                                        onClick={handleCopyLink}
                                    >
                                        {isCopied ? (
                                            <>
                                                <i className="fi fi-rr-check"></i>
                                                <span>Copied!</span>
                                            </>
                                        ) : (
                                            <>
                                                <i className="fi fi-rr-copy-alt"></i>
                                                <span>Copy Link</span>
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="episode-links">
                            <div className="episode-links-play-container">
                                {(() => {
                                    const uiState = getEpisodeButtonUiState();
                                    return (
                                <button 
                                    className="episode-play-button-new" 
                                    onClick={() => {
                                        if (!podcast || !podcast.audioFile) return;
                                        
                                        const savedState = sessionStorage.getItem(STORAGE_KEY);
                                        let shouldPlay = true;
                                        
                                        if (savedState) {
                                            try {
                                                const state = JSON.parse(savedState);
                                                if (state.currentPodcast && isSamePodcast(state.currentPodcast, podcast)) {
                                                    shouldPlay = !state.isPlaying;
                                                }
                                            } catch (e) {}
                                        }
                                        
                                        if (shouldPlay) {
                                            playPodcastGlobal(podcast);
                                        } else {
                                            window.dispatchEvent(new CustomEvent('pausePodcast'));
                                        }
                                    }}
                                    title={uiState.mode === 'completed' ? 'Replay' : (isCurrentPodcast && isGlobalPlaying ? 'Pause' : 'Play')}
                                >
                                    {uiState.mode === 'completed' ? (
                                        <i className="fi fi-br-rotate-right"></i>
                                    ) : uiState.mode === 'progress' ? (
                                        <div className="episode-play-status">
                                            <div className={`episode-mini-eq${uiState.isLive ? ' is-playing' : ''}`}>
                                                <span></span><span></span><span></span><span></span>
                                            </div>
                                            <div className="episode-play-progress-line">
                                                <span style={{ width: `${uiState.progressPct}%` }}></span>
                                            </div>
                                            <span className="episode-left-time">{uiState.leftText} left</span>
                                        </div>
                                    ) : (
                                        <>
                                            <i className="fi fi-sr-play"></i>
                                            <span>Play</span>
                                        </>
                                    )}
                                </button>
                                    );
                                })()}
                                
                                {podcast.episodeLinks && Array.isArray(podcast.episodeLinks) && podcast.episodeLinks.length > 0 && (
                                    <>
                                        <span className="episode-links-or">or</span>
                                        <div className="episode-link-icons-new">
                                            {podcast.episodeLinks.map((link, index) => {
                                                let iconClass = 'fi fi-sr-podcast';
                                                let brandClass = 'brand-default';
                                                if (link.includes('spotify')) { iconClass = 'fi fi-brands-spotify'; brandClass = 'brand-spotify'; }
                                                else if (link.includes('youtube') || link.includes('youtu.be')) { iconClass = 'fi fi-brands-youtube'; brandClass = 'brand-youtube'; }
                                                else if (link.includes('apple')) { iconClass = 'fi fi-sr-podcast'; brandClass = 'brand-apple'; }
                                                
                                                const fullLink = link.startsWith('http') ? link : `https://${link}`;
                                                return (
                                                    <a key={index} href={fullLink} target="_blank" rel="noopener noreferrer" className={`episode-link-icon-new ${brandClass}`} title={link}>
                                                        <i className={iconClass}></i>
                                                    </a>
                                                );
                                            })}
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                        <div className="episode-transcript">
                            <div className="transcript-toggle-container">
                                <button className={`transcript-toggle-btn ${viewMode === 'description' ? 'active' : ''}`} onClick={() => { setViewMode('description'); setShowTranscript(true); }}>Show notes</button>
                                <button className={`transcript-toggle-btn ${viewMode === 'transcript' ? 'active' : ''}`} onClick={() => {
                                    setViewMode('transcript');
                                    setShowTranscript(true);
                                    if (!transcriptData && !transcriptLoading) {
                                        fetchTranscript();
                                    }
                                }}>Transcript</button>
                            </div>
                            {showTranscript && (
                                <div className={`transcript-content ${viewMode}`} ref={transcriptScrollRef} id="episode-transcript-container">
                                    {viewMode === 'transcript' ? (
                                        <>
                                            <div className="transcript-header">
                                                <span className="transcript-header-text">
                                                    Follow the conversation
                                                </span>
                                                {!activeWordInView && (
                                                    <button
                                                        className="back-to-transcript-btn"
                                                        onClick={() => {
                                                            if (transcriptWords.currentIndex >= 0) {
                                                                const element = document.getElementById(`episode-transcript-word-${transcriptWords.currentIndex}`);
                                                                const container = document.getElementById('episode-transcript-scroll-area');
                                                                
                                                                if (element && container) {
                                                                    const containerRect = container.getBoundingClientRect();
                                                                    const elementRect = element.getBoundingClientRect();
                                                                    const scrollTop = container.scrollTop;
                                                                    const elementOffset = elementRect.top - containerRect.top + scrollTop;
                                                                    const centerOffset = (containerRect.height / 2) - (elementRect.height / 2);
                                                                    
                                                                    container.scrollTo({
                                                                        top: elementOffset - centerOffset,
                                                                        behavior: 'smooth'
                                                                    });
                                                                    setActiveWordInView(true);
                                                                    setIsAutoScrollEnabled(true);
                                                                }
                                                            }
                                                        }}
                                                    >
                                                        Back to Transcript
                                                    </button>
                                                )}
                                            </div>
                                            {transcriptLoading ? (
                                                <div className="no-transcript-message-episode">Loading transcript…</div>
                                            ) : transcriptError ? (
                                                <div className="no-transcript-message-episode">
                                                    {transcriptError}
                                                    <button onClick={fetchTranscript} style={{ marginLeft: '8px', cursor: 'pointer' }}>Retry</button>
                                                </div>
                                            ) : (
                                            <div
                                                className="transcript-scroll-area-episode"
                                                id="episode-transcript-scroll-area"
                                                onScroll={checkActiveWordVisibility}
                                                onWheel={handleUserScrollInteraction}
                                                onTouchMove={handleUserScrollInteraction}
                                            >
                                                {transcriptData && transcriptData.transcript && transcriptWords.isNewFormat && transcriptWords.words.length > 0 ? (
                                                    <div className="transcript-timeline-container">
                                                        {(() => {
                                                            // Group words by chapters
                                                            const groups = [];
                                                            const allWords = transcriptWords.words;
                                                            
                                                            // If no chapters, just show one group
                                                            if (!chapters || chapters.length === 0) {
                                                                groups.push({
                                                                    start: 0,
                                                                    title: 'Start',
                                                                    words: allWords
                                                                });
                                                            } else {
                                                                // Include a 'Start' chapter if the first chapter doesn't start at 0
                                                                if (chapters[0].start > 0) {
                                                                    const preWords = allWords.filter(w => w.start < chapters[0].start);
                                                                    if (preWords.length > 0) {
                                                                        groups.push({
                                                                            start: 0,
                                                                            title: 'Introduction',
                                                                            words: preWords
                                                                        });
                                                                    }
                                                                }

                                                                chapters.forEach((chapter, idx) => {
                                                                    const nextChapterTime = chapters[idx + 1] ? chapters[idx + 1].start : Infinity;
                                                                    const chapterWords = allWords.filter(w => {
                                                                        const t = parseFloat(w.start);
                                                                        return t >= chapter.start && t < nextChapterTime;
                                                                    });
                                                                    
                                                                    if (chapterWords.length > 0) {
                                                                        groups.push({
                                                                            start: chapter.start,
                                                                            title: chapter.title,
                                                                            words: chapterWords
                                                                        });
                                                                    }
                                                                });
                                                            }

                                                            return groups.map((group, groupIdx) => {
                                                                // Format time (e.g., 65 -> 01:05)
                                                                const minutes = Math.floor(group.start / 60);
                                                                const seconds = Math.floor(group.start % 60);
                                                                const timeStr = `${minutes}:${seconds.toString().padStart(2, '0')}`;

                                                                // Check if this group is the current one
                                                                const nextGroup = groups[groupIdx + 1];
                                                                const isCurrentGroup = currentTime >= group.start && (!nextGroup || currentTime < nextGroup.start);

                                                                return (
                                                                    <div key={groupIdx} className={`timeline-group ${isCurrentGroup ? 'active' : ''}`}>
                                                                        <div className="timeline-header-row">
                                                                            <span className={`timeline-timestamp ${isCurrentGroup ? 'active' : ''}`}>{timeStr}</span>
                                                                            <div className="timeline-dot-wrapper">
                                                                                <div className={`timeline-dot ${isCurrentGroup ? 'active' : ''}`}></div>
                                                                            </div>
                                                                            <span className={`timeline-chapter-title ${isCurrentGroup ? 'active' : ''}`}>{group.title}</span>
                                                                        </div>
                                                                        <div className="timeline-body">
                                                                            <div className="timeline-line-container">
                                                                                {groupIdx < groups.length - 1 && (
                                                                                    <div className={`timeline-vertical-line ${isCurrentGroup ? 'active' : ''}`}></div>
                                                                                )}
                                                                            </div>
                                                                            <div className="timeline-text-content">
                                                                                {group.words.map((word, wordIdx) => {
                                                                                    const trueIndex = transcriptWords.words.indexOf(word);
                                                                                    const isCurrent = trueIndex === transcriptWords.currentIndex;
                                                                                    const isCompleted = transcriptWords.currentIndex >= 0 && trueIndex <= transcriptWords.currentIndex;
                                                                                    
                                                                                    return (
                                                                                        <span
                                                                                            key={`word-${trueIndex}`}
                                                                                            id={`episode-transcript-word-${trueIndex}`}
                                                                                            className={`transcript-word-episode ${isCurrent ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}
                                                                                            data-start={word.start}
                                                                                        >
                                                                                            {word.word}{' '}
                                                                                        </span>
                                                                                    );
                                                                                })}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            });
                                                        })()}
                                                    </div>
                                                ) : transcriptData && transcriptData.transcript && !transcriptWords.isNewFormat ? (
                                                    <div>
                                                        {transcriptData.transcript.map((line, index) => (
                                                            <p key={index} className="transcript-line-episode">{line.text || ''}</p>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <p className="no-transcript-message-episode">No transcript available for this episode.</p>
                                                )}
                                            </div>
                                            )}
                                        </>
                                    ) : (
                                        <div className="episode-description-content">
                                            {(() => {
        const parseOptions = (domNode) => {
            if (domNode.type === 'tag' && domNode.name === 'span' && domNode.attribs && domNode.attribs['data-type'] === 'blog-link') {
                return <BlogLinkCard url={domNode.attribs['data-url']} text={domNode.attribs['data-text']} />;
            }
            
            // Handle text nodes for timestamps (Format: MM:SS or HH:MM:SS)
            if (domNode.type === 'text' && domNode.data) {
                const timestampRegex = /(\d{1,2}:\d{2}(?::\d{2})?)/g;
                const parts = domNode.data.split(timestampRegex);
                
                if (parts.length > 1) {
                    return (
                        <>
                            {parts.map((part, index) => {
                                if (timestampRegex.test(part)) {
                                    // Calculate seconds
                                    const timeParts = part.split(':').reverse();
                                    let seconds = 0;
                                    if (timeParts[0]) seconds += parseInt(timeParts[0], 10);
                                    if (timeParts[1]) seconds += parseInt(timeParts[1], 10) * 60;
                                    if (timeParts[2]) seconds += parseInt(timeParts[2], 10) * 3600;
                                    
                                    return (
                                        <span 
                                            key={index} 
                                            className="timestamp-link" 
                                            style={{ color: '#47b5ff', cursor: 'pointer', fontWeight: 'bold' }}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                playPodcastGlobal(podcast, seconds);
                                            }}
                                        >
                                            {part}
                                        </span>
                                    );
                                }
                                return part;
                            })}
                        </>
                    );
                }
            }
        };

        const renderOverview = () => {
            if (!podcast || !podcast.overview) {
                return <p className="no-description-message-episode">No show notes available for this episode.</p>;
            }

            const extractText = (node) => {
                if (!node) return '';
                if (typeof node === 'string') return node;
                if (node.text) return node.text;
                if (node.content && Array.isArray(node.content)) {
                    return node.content.map(extractText).join(node.type === 'paragraph' ? '\n' : ' ');
                }
                return '';
            };

            try {
                // 1. Try to render TipTap HTML first (Best quality, preserves alignment/fonts)
                const html = renderTiptapHTML(podcast.overview);
                if (html && html.trim()) {
                    console.log("[PodcastEpisode] Rendering TipTap HTML for overview");
                    return (
                        <div className="episode-description-container">
                            {parseHtml(html, parseOptions)}
                        </div>
                    );
                }

                // 2. Fallback to extracting text from JSON if HTML is empty
                const jsonContent = typeof podcast.overview.json === 'string' 
                    ? JSON.parse(podcast.overview.json) 
                    : (podcast.overview.json || (typeof podcast.overview === 'object' && !podcast.overview.html ? podcast.overview : null));
                
                if (jsonContent) {
                    const rawText = extractText(jsonContent);
                    if (rawText && rawText.trim()) {
                        console.log("[PodcastEpisode] Rendering extracted text for overview");
                        return <div className="episode-description-text" style={{ whiteSpace: 'pre-wrap' }}>{rawText}</div>;
                    }
                }

                // 3. Last resort fallbacks
                if (podcast.overview.html) {
                    return (
                        <div className="episode-description-container">
                            {parseHtml(podcast.overview.html, parseOptions)}
                        </div>
                    );
                }
                
                const rawValue = typeof podcast.overview === 'string' ? podcast.overview : '';
                return <div className="episode-description-text" style={{ whiteSpace: 'pre-wrap' }}>{rawValue}</div>;

            } catch (e) {
                console.error("Error rendering show notes:", e);
                const fallback = typeof podcast.overview === 'string' ? podcast.overview : 'Error loading show notes.';
                return <div className="episode-description-text">{fallback}</div>;
            }
        };

        return renderOverview();
    })()}
                                        </div>
                                    )}
                                </div>
                            )}
                            {podcast._id && <PodcastThoughts podcastId={podcast._id} />}
                        </div>
                    </div>

                    <SidebarContentGroup
                        className="podcast-episode-sidebar lg:sticky lg:top-24 lg:self-start"
                        sections={["ad", "subscribe", "followMe"]}
                    />
                    {/* Author Popup */}
                    {isPopupOpen && selectedAuthors.length > 0 && (
                        <AuthorPopup 
                            authors={selectedAuthors} 
                            initialAuthorIndex={selectedAuthorIndex}
                            isOpen={isPopupOpen} 
                            onClose={handlePopupClose} 
                        />
                    )}
                </div>
            </div>
            <Footer />
        </>
    );
}

// --- Social link helpers (matching Post.js) ---
function getSocialMediaPlatform(url) {
    if (!url || typeof url !== 'string') return null;
    try {
        let urlToParse = url.trim();
        if (!urlToParse.match(/^https?:\/\//i)) urlToParse = 'https://' + urlToParse;
        const hostname = new URL(urlToParse).hostname.toLowerCase().replace(/^www\./, '');
        const platformMap = {
            'github.com': 'github', 'linkedin.com': 'linkedin', 'instagram.com': 'instagram',
            'youtube.com': 'youtube', 'twitter.com': 'twitter', 'x.com': 'twitter',
            'threads.net': 'threads', 'threads.com': 'threads', 'facebook.com': 'facebook',
            'fb.com': 'facebook', 'tiktok.com': 'tiktok', 'pinterest.com': 'pinterest',
            'snapchat.com': 'snapchat', 'reddit.com': 'reddit', 'medium.com': 'medium',
            'behance.net': 'behance', 'dribbble.com': 'dribbble', 'discord.com': 'discord',
            'telegram.org': 'telegram', 'whatsapp.com': 'whatsapp'
        };
        return platformMap[hostname] || null;
    } catch (e) { return null; }
}

function getSocialIconClass(platform) {
    const iconMap = {
        'github': 'fi fi-brands-github', 'linkedin': 'fi fi-brands-linkedin',
        'instagram': 'fi fi-brands-instagram', 'youtube': 'fi fi-brands-youtube',
        'twitter': 'fi fi-brands-twitter-alt', 'threads': 'fi fi-brands-threads',
        'facebook': 'fi fi-brands-facebook', 'tiktok': 'fi fi-brands-tiktok',
        'pinterest': 'fi fi-brands-pinterest', 'snapchat': 'fi fi-brands-snapchat',
        'reddit': 'fi fi-brands-reddit', 'medium': 'fi fi-brands-medium',
        'behance': 'fi fi-brands-behance', 'dribbble': 'fi fi-brands-dribbble',
        'discord': 'fi fi-brands-discord', 'telegram': 'fi fi-brands-telegram',
        'whatsapp': 'fi fi-brands-whatsapp'
    };
    return iconMap[platform] || 'fi fi-brands-link';
}

function formatBioWithLinks(bio) {
    if (!bio) return null;
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = bio.split(urlRegex);
    return parts.map((part, index) => {
        if (urlRegex.test(part)) {
            return (
                <a key={index} href={part} target="_blank" rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}>{part}</a>
            );
        }
        return part;
    });
}

// AuthorPopup component matching the Post.js design
const AuthorPopup = ({ authors, initialAuthorIndex = 0, isOpen, onClose }) => {
    const [currentIndex, setCurrentIndex] = useState(initialAuthorIndex);

    useEffect(() => {
        if (isOpen) setCurrentIndex(initialAuthorIndex);
    }, [isOpen, initialAuthorIndex]);

    // Lock background scroll when popup is open
    useEffect(() => {
        if (isOpen) {
            const scrollY = window.scrollY;
            document.body.style.position = 'fixed';
            document.body.style.top = `-${scrollY}px`;
            document.body.style.width = '100%';
            document.body.style.overflow = 'hidden';
            return () => {
                document.body.style.position = '';
                document.body.style.top = '';
                document.body.style.width = '';
                document.body.style.overflow = '';
                window.scrollTo(0, scrollY);
            };
        }
    }, [isOpen]);

    const author = authors && authors[currentIndex];
    if (!isOpen || !author) return null;

    const handleNext = (e) => {
        e.stopPropagation();
        setCurrentIndex(prev => (prev < authors.length - 1 ? prev + 1 : 0));
    };

    const handlePrev = (e) => {
        e.stopPropagation();
        setCurrentIndex(prev => (prev > 0 ? prev - 1 : authors.length - 1));
    };

    const socialLinks = (author.socialLinks || [])
        .map(link => {
            if (!link || typeof link !== 'string') return null;
            let url = link.trim();
            if (!url.match(/^https?:\/\//i)) url = 'https://' + url;
            const platform = getSocialMediaPlatform(url);
            if (!platform) return null;
            return { url, platform, iconClass: getSocialIconClass(platform) };
        })
        .filter(Boolean);

    return (
        <div className="author-popup-overlay" onClick={onClose}>
            <div className="author-popup" onClick={e => e.stopPropagation()}>
                <div className="author-popup-header">
                    <span className="author-popup-title">Profile</span>
                    <div className="author-popup-closebtn" onClick={onClose}>
                        <p>Esc</p>
                        <i id="close_btn" className="fi fi-rr-cross-small"></i>
                    </div>
                </div>
                <div className="author-popup-divider"></div>
                <div className="author-popup-content-wrapper">
                    {/* Left side – Image, Name, Bio */}
                    <div className="author-popup-left">
                        <img
                            className="author-popup-image"
                            src={author.image || "/images/home_images/Logo.png"}
                            alt={`${author.name} profile`}
                            onError={(e) => { e.target.src = "/images/home_images/Logo.png"; }}
                        />
                        <div className="author-popup-name">{author.name}</div>
                        <div className="author-popup-bio">{formatBioWithLinks(author.bio)}</div>
                    </div>
                    {/* Right side – Social Media Icons */}
                    <div className="author-popup-right">
                        {socialLinks.length > 0 ? (
                            <div className="author-popup-social-icons">
                                {socialLinks.map((social, idx) => (
                                    <a
                                        key={idx}
                                        href={social.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={`author-popup-social-icon ${social.platform}`}
                                        title={social.platform.charAt(0).toUpperCase() + social.platform.slice(1)}
                                    >
                                        <i className={social.iconClass}></i>
                                    </a>
                                ))}
                            </div>
                        ) : (
                            <div className="author-popup-no-social" style={{ fontSize: '12px', color: '#999', textAlign: 'center', padding: '10px' }}>
                                No social links
                            </div>
                        )}
                    </div>
                </div>
                {/* Navigation Arrows – only if multiple hosts */}
                {authors.length > 1 && (
                    <div className="author-popup-navigation">
                        <button className="author-nav-btn prev" onClick={handlePrev} title="Previous Host">
                            <i className="fi fi-rr-angle-left"></i>
                        </button>
                        <div className="author-nav-dots">
                            {authors.map((_, idx) => (
                                <span
                                    key={idx}
                                    className={`author-nav-dot ${idx === currentIndex ? 'active' : ''}`}
                                    onClick={(e) => { e.stopPropagation(); setCurrentIndex(idx); }}
                                />
                            ))}
                        </div>
                        <button className="author-nav-btn next" onClick={handleNext} title="Next Host">
                            <i className="fi fi-rr-angle-right"></i>
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};
